// src/lib/kinema/konva/interaction/KonvaEventAdapter.ts
import Konva from 'konva';
import type { Editor } from '../../core/Editor';
import type { ToolManager } from '../../core/tools/ToolManager';
import type { EditorPointerEvent, Point, Rect, TransformResult } from '../../core/tools/types';
import type { SnapGuides } from '../overlays/SnapGuides';
import type { MarqueeOverlay } from '../overlays/MarqueeOverlay';
import type { PenOverlay } from '../overlays/PenOverlay';
import type { PathEditOverlay } from '../overlays/PathEditOverlay';
import type { SelectionOverlay } from '../overlays/SelectionOverlay';
import { SelectTool } from '../../core/tools/SelectTool';
import { PenTool } from '../../core/tools/PenTool';
import { getCompositionProps } from '../../core/types';
import { calculateSnap } from '../../core/tools/snapCalculation';

export class KonvaEventAdapter {
  private wiredNodes = new Set<string>();
  private nodeMap: Map<string, Konva.Node> = new Map();
  private snapGuides: SnapGuides | null = null;
  private marqueeOverlay: MarqueeOverlay | null = null;
  private penOverlay: PenOverlay | null = null;
  private pathEditOverlay: PathEditOverlay | null = null;
  private selectionOverlay: SelectionOverlay | null = null;
  private snapDisabled = false;

  constructor(
    private stage: Konva.Stage,
    private toolManager: ToolManager,
    private editor: Editor,
    private transformer: Konva.Transformer,
  ) {
    this.setupStageEvents();
    this.setupTransformerEvents();

    // Hold Cmd/Ctrl to disable snapping during drag
    const onKeyDown = (e: KeyboardEvent) => { if (e.metaKey || e.ctrlKey) this.snapDisabled = true; };
    const onKeyUp = (e: KeyboardEvent) => { if (!e.metaKey && !e.ctrlKey) this.snapDisabled = false; };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('keyup', onKeyUp);
  }

  syncEventListeners(nodeMap: Map<string, Konva.Node>): void {
    this.nodeMap = nodeMap;
    this.syncToolState();

    for (const [layerId, node] of nodeMap) {

      if (this.wiredNodes.has(layerId)) continue;

      node.on('mousedown touchstart', (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
        e.cancelBubble = true;
        const tool = this.toolManager.getActiveTool();

        // Clicking a different layer while in path edit → exit path edit
        if (tool instanceof SelectTool && tool.isEditingPath() && tool.getEditingPathLayerId() !== layerId) {
          tool.exitPathEditMode();
          this.transformer.visible(true);
          this.syncToolState();
          this.syncPathEditOverlay();
        }

        const normalized = this.normalizeEvent(e);
        tool.onPointerDown(normalized, { layerId, point: normalized.point });
      });

      node.on('dragstart', () => {
        const tool = this.toolManager.getActiveTool();
        const selectedIds = Array.from(this.editor.getState().selectedIds);
        // If dragged layer isn't selected, select only it
        if (!selectedIds.includes(layerId)) {
          tool.onDragStart?.([layerId]);
        } else {
          tool.onDragStart?.(selectedIds);
        }
      });

      node.on('dragmove', () => {
        this.syncMultiDragMove(layerId);
        this.applySnapDuringDrag(layerId);
        this.selectionOverlay?.refreshAnchorPosition();
      });

      node.on('dragend', () => {
        const tool = this.toolManager.getActiveTool();
        const selectedIds = Array.from(this.editor.getState().selectedIds);
        const ids = selectedIds.includes(layerId) ? selectedIds : [layerId];
        const finalPositions = new Map<string, Point>();
        for (const id of ids) {
          const n = this.nodeMap.get(id);
          if (n) finalPositions.set(id, { x: n.x(), y: n.y() });
        }
        this.dragStartPositions.clear();
        this.snapGuides?.clear();
        tool.onDragEnd?.(ids, finalPositions);
      });

      node.setAttr('layerId', layerId);
      this.wiredNodes.add(layerId);
    }

    // Clean up removed nodes
    for (const layerId of this.wiredNodes) {
      if (!nodeMap.has(layerId)) {
        this.wiredNodes.delete(layerId);
      }
    }
  }

  private dragStartPositions = new Map<string, Point>();

  private syncMultiDragMove(primaryId: string): void {
    const selectedIds = Array.from(this.editor.getState().selectedIds);
    if (selectedIds.length <= 1) return;

    const primaryNode = this.nodeMap.get(primaryId);
    if (!primaryNode) return;

    // On first dragmove, capture all start positions
    if (this.dragStartPositions.size === 0) {
      for (const id of selectedIds) {
        const n = this.nodeMap.get(id);
        if (n) this.dragStartPositions.set(id, { x: n.x(), y: n.y() });
      }
    }

    const primaryStart = this.dragStartPositions.get(primaryId);
    if (!primaryStart) return;
    const dx = primaryNode.x() - primaryStart.x;
    const dy = primaryNode.y() - primaryStart.y;

    for (const id of selectedIds) {
      if (id === primaryId) continue;
      const n = this.nodeMap.get(id);
      const start = this.dragStartPositions.get(id);
      if (n && start) {
        n.x(start.x + dx);
        n.y(start.y + dy);
      }
    }
  }

  /** Sync interactivity based on current tool. */
  syncToolState(): void {
    const isSelect = this.toolManager.getActiveTool().name === 'select';
    // When not in select mode, disable all node + transformer listening
    // so clicks/drags pass through to the stage for panning etc.
    // The Transformer stays visible — just non-interactive.
    for (const [, node] of this.nodeMap) {
      node.draggable(isSelect);
      node.listening(isSelect);
    }
    this.transformer.listening(isSelect);
  }

  private setupStageEvents(): void {
    this.stage.on('mousedown touchstart', (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
      if (e.target !== this.stage) return;
      const tool = this.toolManager.getActiveTool();

      // Click on empty space while in path edit mode → exit and commit
      if (tool instanceof SelectTool && tool.isEditingPath()) {
        tool.exitPathEditMode();
        this.transformer.visible(true);
        this.syncToolState();
        this.syncPathEditOverlay();
        return;
      }

      const normalized = this.normalizeEvent(e);
      tool.onPointerDown(normalized, { layerId: null, point: normalized.point });
    });

    this.stage.on('mousemove touchmove', (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
      const normalized = this.normalizeEvent(e);
      this.toolManager.getActiveTool().onPointerMove(normalized);
      this.syncMarqueeOverlay();
      this.syncPenOverlay(normalized.point);
    });

    this.stage.on('mouseup touchend', (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
      this.toolManager.getActiveTool().onPointerUp(this.normalizeEvent(e));
      this.dragStartPositions.clear();
      this.syncMarqueeOverlay();
      this.syncPenOverlay();
    });

    // Zoom: scroll wheel zooms around cursor position (tool-independent)
    const container = this.stage.container();
    container.addEventListener('wheel', (e: WheelEvent) => {
      e.preventDefault();
      const scaleBy = 1.05;
      const oldScale = this.stage.scaleX();
      const pointer = this.stage.getPointerPosition();
      if (!pointer) return;

      const newScale = e.deltaY < 0 ? oldScale * scaleBy : oldScale / scaleBy;
      // Clamp zoom between 10% and 500%
      const clampedScale = Math.max(0.1, Math.min(5, newScale));

      // Zoom around cursor position
      const mousePointTo = {
        x: (pointer.x - this.stage.x()) / oldScale,
        y: (pointer.y - this.stage.y()) / oldScale,
      };

      this.stage.scale({ x: clampedScale, y: clampedScale });
      this.stage.position({
        x: pointer.x - mousePointTo.x * clampedScale,
        y: pointer.y - mousePointTo.y * clampedScale,
      });
      this.stage.batchDraw();
    }, { passive: false });
  }

  // Stored on transformstart so we can correct position each frame
  private transformStartState = new Map<string, {
    x: number; y: number; scaleX: number; scaleY: number;
    anchorX: number; anchorY: number;
  }>();
  private prevTransformScale = new Map<string, { x: number; y: number }>();

  private setupTransformerEvents(): void {
    // Double-click via DOM (bypasses Konva/Transformer event issues)
    this.stage.container().addEventListener('dblclick', () => {
      const tool = this.toolManager.getActiveTool();
      if (tool instanceof SelectTool && !tool.isEditingPath()) {
        const selectedId = this.editor.getState().primarySelectedId;
        if (selectedId) {
          const layer = this.editor.getState().layers[selectedId];
          if (layer?.type === 'shape' && layer.shape === 'path') {
            tool.enterPathEditMode(selectedId);
            // Hide transformer and disable node dragging in path edit mode
            this.transformer.nodes([]);
            this.transformer.visible(false);
            for (const [, node] of this.nodeMap) {
              node.draggable(false);
            }
            this.syncPathEditOverlay();
          }
        }
      }
    });

    this.transformer.on('transformstart', () => {
      const tool = this.toolManager.getActiveTool();
      const nodes = this.transformer.nodes();
      const layerIds: string[] = [];

      // Capture start state for anchor-based scaling correction
      this.transformStartState.clear();
      for (const node of nodes) {
        const layerId = node.getAttr('layerId') as string;
        if (!layerId) continue;
        layerIds.push(layerId);
        this.transformStartState.set(layerId, {
          x: node.x(),
          y: node.y(),
          scaleX: node.scaleX(),
          scaleY: node.scaleY(),
          anchorX: node.offsetX(),
          anchorY: node.offsetY(),
        });
      }
      // Track previous scale for rate limiting
      this.prevTransformScale.clear();
      for (const node of nodes) {
        const layerId = node.getAttr('layerId') as string;
        if (layerId) this.prevTransformScale.set(layerId, { x: node.scaleX(), y: node.scaleY() });
      }

      tool.onTransformStart?.(layerIds);
    });

    // Correct position every frame so scaling happens around the anchor,
    // not around the opposite corner (which is Konva Transformer's default).
    this.transformer.on('transform', (e) => {
      const altHeld = (e.evt as MouseEvent)?.altKey ?? false;

      for (const node of this.transformer.nodes()) {
        const layerId = node.getAttr('layerId') as string;
        const start = this.transformStartState.get(layerId);
        if (!start) continue;

        const layer = this.editor.getState().layers[layerId];
        if (!layer) continue;

        // Edge clamp: if anchor is on an edge, prevent scaling from that side
        // by reverting scale to previous value
        const anchorOnLeft = start.anchorX <= 1;
        const anchorOnRight = layer.width !== undefined && Math.abs(start.anchorX - layer.width) <= 1;
        const anchorOnTop = start.anchorY <= 1;
        const anchorOnBottom = layer.height !== undefined && Math.abs(start.anchorY - layer.height) <= 1;

        const prev = this.prevTransformScale.get(layerId) ?? { x: start.scaleX, y: start.scaleY };
        let sx = node.scaleX();
        let sy = node.scaleY();

        // Prevent negative scaling (no flipping)
        sx = Math.max(0.01, sx);
        sy = Math.max(0.01, sy);

        // Clamp when anchor is on the edge being dragged
        if (anchorOnLeft || anchorOnRight) {
          if (sx > prev.x * 3) sx = prev.x;
        }
        if (anchorOnTop || anchorOnBottom) {
          if (sy > prev.y * 3) sy = prev.y;
        }

        // Alt/Option: fine scale — limit change per frame
        if (altHeld) {
          const maxDelta = 0.005;
          sx = prev.x + Math.max(-maxDelta, Math.min(maxDelta, sx - prev.x));
          sy = prev.y + Math.max(-maxDelta, Math.min(maxDelta, sy - prev.y));
          sx = Math.max(0.01, sx);
          sy = Math.max(0.01, sy);
        }

        node.scaleX(sx);
        node.scaleY(sy);
        this.prevTransformScale.set(layerId, { x: sx, y: sy });

        const scaleChanged = sx !== start.scaleX || sy !== start.scaleY;

        if (scaleChanged) {
          const layerType = layer.type;

          if (layerType === 'text' || layerType === 'shape') {
            const offsetX = node.offsetX();
            const offsetY = node.offsetY();

            const topLeftX = node.x() - offsetX * sx;
            const topLeftY = node.y() - offsetY * sy;

            const newW = node.width() * sx;
            const newH = node.height() * sy;
            node.scaleX(1);
            node.scaleY(1);
            node.width(newW);
            node.height(newH);

            const newOffsetX = offsetX * sx;
            const newOffsetY = offsetY * sy;
            node.offsetX(newOffsetX);
            node.offsetY(newOffsetY);

            node.x(topLeftX + newOffsetX);
            node.y(topLeftY + newOffsetY);

            // Reset prev scale tracking (scale was flattened to 1)
            this.prevTransformScale.set(layerId, { x: 1, y: 1 });
          } else {
            node.x(start.x);
            node.y(start.y);
          }
        }
      }
      this.selectionOverlay?.refreshAnchorPosition();
    });

    this.transformer.on('transformend', () => {
      const tool = this.toolManager.getActiveTool();
      const nodes = this.transformer.nodes();
      const layerIds: string[] = [];
      const finalBounds = new Map<string, TransformResult>();

      for (const node of nodes) {
        const layerId = node.getAttr('layerId') as string;
        if (!layerId) continue;
        layerIds.push(layerId);

        const layerType = this.editor.getState().layers[layerId]?.type;
        const result: TransformResult = {
          x: node.x(),
          y: node.y(),
          scaleX: node.scaleX(),
          scaleY: node.scaleY(),
          rotation: node.rotation(),
        };

        // For text/shape layers, include width/height/anchor (scale was flattened during transform)
        if (layerType === 'text' || layerType === 'shape') {
          result.width = node.width();
          result.height = node.height();
          result.anchorX = node.offsetX();
          result.anchorY = node.offsetY();
        }

        finalBounds.set(layerId, result);
      }

      this.transformStartState.clear();
      tool.onTransformEnd?.(layerIds, finalBounds);
    });
  }

  private normalizeEvent(e: Konva.KonvaEventObject<MouseEvent | TouchEvent>): EditorPointerEvent {
    const nativeEvent = e.evt;
    const rawPos = this.stage.getPointerPosition();
    const screenPoint = rawPos ? { x: rawPos.x, y: rawPos.y } : { x: 0, y: 0 };

    // Convert to canvas coordinates (accounts for pan + zoom)
    const scale = this.stage.scaleX() || 1;
    const canvasPoint = {
      x: (screenPoint.x - this.stage.x()) / scale,
      y: (screenPoint.y - this.stage.y()) / scale,
    };

    return {
      point: canvasPoint,
      screenPoint,
      shiftKey: nativeEvent.shiftKey ?? false,
      metaKey: nativeEvent.metaKey ?? false,
    };
  }

  setSnapGuides(snapGuides: SnapGuides): void {
    this.snapGuides = snapGuides;
  }

  setMarqueeOverlay(marqueeOverlay: MarqueeOverlay): void {
    this.marqueeOverlay = marqueeOverlay;
  }

  private syncMarqueeOverlay(): void {
    if (!this.marqueeOverlay) return;
    const tool = this.toolManager.getActiveTool();
    if (tool instanceof SelectTool) {
      const rect = tool.getMarqueeRect();
      if (rect) {
        this.marqueeOverlay.showMarquee(rect);
      } else {
        this.marqueeOverlay.clear();
      }
    }
  }

  setPenOverlay(penOverlay: PenOverlay): void {
    this.penOverlay = penOverlay;
  }

  setPathEditOverlay(overlay: PathEditOverlay): void {
    this.pathEditOverlay = overlay;
  }

  setSelectionOverlay(overlay: SelectionOverlay): void {
    this.selectionOverlay = overlay;
  }

  private syncPathEditOverlay(): void {
    if (!this.pathEditOverlay) return;
    const tool = this.toolManager.getActiveTool();
    if (tool instanceof SelectTool && tool.isEditingPath()) {
      const layerId = tool.getEditingPathLayerId()!;
      const layer = this.editor.getState().layers[layerId];
      const points = tool.getEditingPathPoints();
      if (layer && points) {
        const offsetX = layer.x - layer.anchorX * layer.scaleX;
        const offsetY = layer.y - layer.anchorY * layer.scaleY;

        this.pathEditOverlay.setCallbacks({
          onPointDragMove: () => {},
          onPointDragEnd: (idx, x, y) => { tool.handlePointDrag(idx, x, y); this.syncPathEditOverlay(); },
          onHandleDragMove: () => {},
          onHandleDragEnd: (idx, type, dx, dy, altKey) => { tool.handleHandleDrag(idx, type, dx, dy, altKey); this.syncPathEditOverlay(); },
          onPointClick: (idx) => { tool.handlePointClick(idx); this.syncPathEditOverlay(); },
          onSegmentClick: (afterIdx) => { tool.handleSegmentClick(afterIdx); this.syncPathEditOverlay(); },
        });
        this.pathEditOverlay.show(points, tool.isEditingPathClosed(), offsetX, offsetY, layer.scaleX, layer.scaleY);
        this.pathEditOverlay.setSelectedPoint(tool.getSelectedPointIndex());
      }
    } else {
      this.pathEditOverlay.hide();
    }
  }

  private syncPenOverlay(currentMouse?: { x: number; y: number }): void {
    if (!this.penOverlay) return;
    const tool = this.toolManager.getActiveTool();
    if (tool instanceof PenTool) {
      const points = tool.getPoints();
      if (points.length > 0) {
        const nearFirst = currentMouse ? tool.isNearFirstPoint(currentMouse) : false;
        this.penOverlay.update(points, currentMouse, nearFirst);
      } else {
        this.penOverlay.clear();
      }
    } else {
      this.penOverlay.clear();
    }
  }

  /** Convert a Konva node's client rect to canvas space */
  private nodeToCanvasRect(node: Konva.Node): Rect {
    const box = node.getClientRect();
    const scale = this.stage.scaleX() || 1;
    const stageX = this.stage.x();
    const stageY = this.stage.y();
    return {
      x: (box.x - stageX) / scale,
      y: (box.y - stageY) / scale,
      width: box.width / scale,
      height: box.height / scale,
    };
  }

  private applySnapDuringDrag(layerId: string): void {
    const tool = this.toolManager.getActiveTool();
    if (!tool.onDragMove || this.snapDisabled) {
      this.snapGuides?.clear();
      return;
    }

    const node = this.nodeMap.get(layerId);
    if (!node) return;

    // Get visual bounding box of the dragged node (in canvas space)
    const movingBounds = this.nodeToCanvasRect(node);

    // Build bounding rects for other layers + canvas from Konva nodes
    const otherBounds: Rect[] = [];
    const state = this.editor.getState();
    const comp = getCompositionProps(state);

    // Canvas edges + center
    otherBounds.push({ x: 0, y: 0, width: comp.width, height: comp.height });
    otherBounds.push({ x: comp.width / 2, y: comp.height / 2, width: 0, height: 0 });

    // Other visible, non-selected layers — use their Konva bounding boxes
    for (const [id, otherNode] of this.nodeMap) {
      if (id === layerId) continue;
      if (state.selectedIds.has(id)) continue;
      const layer = state.layers[id];
      if (!layer?.visible) continue;
      otherBounds.push(this.nodeToCanvasRect(otherNode));
    }

    // Run snap calculation directly on visual bounding boxes
    const snapResult = calculateSnap(movingBounds, otherBounds, 10);

    if (snapResult.snappedX !== undefined || snapResult.snappedY !== undefined) {
      // Calculate how much to shift the node
      const dx = (snapResult.snappedX !== undefined ? snapResult.snappedX - movingBounds.x : 0);
      const dy = (snapResult.snappedY !== undefined ? snapResult.snappedY - movingBounds.y : 0);

      // Round to avoid sub-pixel drift from floating-point scale conversions
      node.x(Math.round((node.x() + dx) * 100) / 100);
      node.y(Math.round((node.y() + dy) * 100) / 100);

      // Apply same delta to other selected nodes
      if (dx !== 0 || dy !== 0) {
        for (const id of state.selectedIds) {
          if (id === layerId) continue;
          const otherNode = this.nodeMap.get(id);
          if (otherNode) {
            otherNode.x(otherNode.x() + dx);
            otherNode.y(otherNode.y() + dy);
          }
        }
      }

      const stageSize = this.stage.size();
      this.snapGuides?.showGuides(snapResult.guides, stageSize.height, stageSize.width);
    } else {
      this.snapGuides?.clear();
    }
  }
}
